/**
 * Why the app was not here a moment ago — asked once, at every launch.
 *
 * Android only. The shell's `AppExitPlugin` answers two different things: one is
 * the WebView's renderer dying under an app the shell then rebuilt around a new
 * one; the other is Android's own record of how the previous process died,
 * which is the only account of a kill nothing of ours was running for.
 *
 * Both used to be filed as `crash`, on the reasoning that from the chat list
 * they are one complaint — «открыл, и оно свернулось само». Three days of the
 * real table said otherwise: fifty-one such rows, not one of them a fault. A
 * renderer reclaimed for its memory, a process trimmed on a skin that trims
 * aggressively, an app updated, an app swiped out of recents — all of it landed
 * under the one word that is supposed to mean «сломалось», and `crash` became
 * the largest group in /admin while the count of things actually broken was
 * zero. So the two are sorted here instead: `lifecycle` for the phone managing
 * its memory and its owner managing their phone, `crash` for what is left.
 * Nothing is dropped — a row that was worth keeping is still kept, under a name
 * that does not cry wolf.
 *
 * The renderer notes are events and are drained — each is reported once because
 * the shell hands each over once. The exit record is a standing fact, the same
 * on every launch until the app dies again, so what is remembered here is its
 * timestamp; that is `reportPushDiagnostics` and its signature again, for the
 * same reason.
 */
import { isNative } from '../api/origin.ts';
import type { ClientErrorKind } from './errorLog.ts';
import { reportClientError } from './errorReporter.ts';
import { withTimeout, PLUGIN_TIMEOUT_MS } from './gallery.ts';

/** The plugin's name on the bridge; there is no iOS half. */
const EXIT_PLUGIN = 'AppExit';

interface AppExitPlugin {
  read(): Promise<AppExitState>;
}

export interface AppExitState {
  /** Renderer deaths the shell survived since the page last asked. */
  renderer?: string[];
  /** Android's account of the previous process's death, if it keeps one. */
  exit?: { at: number; line: string } | null;
}

interface ExitReport {
  kind: ClientErrorKind;
  detail: string;
  message: string;
}

/**
 * The deaths that are the system doing its job rather than this app failing at
 * its own.
 *
 * A whitelist, and deliberately not the other way round: a reason nobody here
 * has heard of — a newer Android's addition, or the bare number this shell
 * answers with below API 30 — comes out as `crash`. Unrecognised is not the same
 * as harmless, and of the two ways to be wrong about a word that means
 * «сломалось», crying wolf is the recoverable one.
 *
 * `other` earns its place on the evidence: on this phone it arrives as
 * `reason=other · MiuiMemoryService(cch-empty)`, which is a memory trim wearing
 * the catch-all. `resource-usage` is deliberately absent — killed for using too
 * much is our fault, and the one kill on this list we would want to be told
 * about twice.
 */
const LIFECYCLE_REASONS = new Set([
  'low-memory',
  'signalled',
  'freezer',
  'package-updated',
  'package-state',
  'user-requested',
  'user-stopped',
  'other',
]);

/** Which of the two a `reason=…` line is, read off the line the shell built. */
function exitKind(line: string): ClientErrorKind {
  const reason = /reason=(\S+)/.exec(line)?.[1];
  return reason !== undefined && LIFECYCLE_REASONS.has(reason) ? 'lifecycle' : 'crash';
}

function plugin(): AppExitPlugin | null {
  if (!isNative) return null;
  const injected = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } })
    .Capacitor?.Plugins?.[EXIT_PLUGIN];
  return (injected as AppExitPlugin | undefined) ?? null;
}

/**
 * What is worth filing, given what the shell answered and the timestamp filed
 * last time, and what to remember instead of it.
 *
 * Exported for the test; nothing else has a use for it.
 */
export function exitReports(
  state: AppExitState,
  filed: string | null,
): { reports: ExitReport[]; filed: string | null } {
  // Always `lifecycle`: this note exists *because* the shell survived the death
  // and rebuilt the WebView around a new renderer. `didCrash=true` is a renderer
  // that genuinely crashed rather than one reclaimed — worth reading, and still
  // not a death of this app, which is what `crash` is for.
  const reports: ExitReport[] = (state.renderer ?? []).map((line) => ({
    kind: 'lifecycle' as const,
    detail: 'render-process',
    message: line,
  }));

  const exit = state.exit;
  if (!exit) return { reports, filed };
  const at = String(exit.at);
  // Every launch sees the same record; only the first one after it is news.
  if (at === filed) return { reports, filed };
  reports.push({ kind: exitKind(exit.line), detail: 'process-exit', message: exit.line });
  return { reports, filed: at };
}

const FILED_KEY = 'chat:app-exit';

/**
 * Called once on the way up, beside the error reporter that carries what it
 * files. Silent everywhere except a shell that has the plugin, which for now is
 * Android only.
 */
export async function reportAppExit(): Promise<void> {
  const exit = plugin();
  if (!exit) return;
  try {
    const state = await withTimeout(exit.read(), PLUGIN_TIMEOUT_MS, 'выход');
    let filed: string | null = null;
    try {
      filed = localStorage.getItem(FILED_KEY);
    } catch {
      // Storage refused; the record is filed again next launch, which is a
      // duplicate row rather than a lost one.
    }
    const result = exitReports(state, filed);
    for (const report of result.reports) {
      reportClientError({ kind: report.kind, detail: report.detail, message: report.message });
    }
    if (result.filed !== filed) {
      try {
        localStorage.setItem(FILED_KEY, result.filed!);
      } catch {
        // As above.
      }
    }
  } catch {
    // A shell without the plugin, or one that cannot answer, is not worth a row
    // of its own.
  }
}
